import { format as formatNumber } from 'd3-format'
import {
  BankReconColumnId,
  BankReconFormula,
  BankReconReferenceIndexEntry,
  BankReconRow,
  BankReconTableUISchema,
} from './types'

export const DEFAULT_COLUMN_LABELS: Record<BankReconColumnId, string> = {
  bucket: 'Bucket',
  classification: 'Classification',
  amount: 'Amount',
  ref: 'Ref',
  formula: 'Formula',
}

export function formatBankReconAmount(value: number, amountFormat?: string): string {
  if (amountFormat) {
    try {
      return formatNumber(amountFormat)(value)
    } catch {
      // fall through to locale formatting
    }
  }
  return new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)
}

export function getColumnLabel(tableUi: BankReconTableUISchema, columnId: BankReconColumnId): string {
  return tableUi.columnLabels?.[columnId] ?? DEFAULT_COLUMN_LABELS[columnId]
}

export function buildFormulaTooltip(formula: BankReconFormula, amountFormat?: string): string {
  if (formula.tooltip) return formula.tooltip
  if (!formula.terms?.length) return formula.expression

  const parts: string[] = []
  for (const term of formula.terms) {
    const formattedValue = formatBankReconAmount(term.value, amountFormat)
    if (parts.length === 0) {
      parts.push(formattedValue)
    } else {
      const operator = term.operator ?? '+'
      parts.push(`${operator} ${formattedValue}`)
    }
  }

  const expression = parts.join(' ')
  const total = formula.terms.reduce((sum, term, index) => {
    const signedValue = index === 0 ? term.value : term.operator === '-' ? -term.value : term.value
    return sum + signedValue
  }, 0)

  return `${expression} = ${formatBankReconAmount(total, amountFormat)}`
}

export function buildReferenceIndex(rows: BankReconRow[], explicit?: BankReconReferenceIndexEntry[]): BankReconReferenceIndexEntry[] {
  if (explicit?.length) return explicit

  const seen = new Set<string>()
  const entries: BankReconReferenceIndexEntry[] = []

  for (const row of rows) {
    if (!row.ref || seen.has(row.ref)) continue
    seen.add(row.ref)
    entries.push({ ref: row.ref, description: row.classification })
  }

  return entries
}

/** Strip grouping spaces (incl. narrow no-break space used in ZAR formatting). */
function stripGroupingSpaces(value: string): string {
  return value.replace(/[\s\u00A0\u202F]/g, '')
}

export function parseEditableAmount(raw: string): number | null {
  const trimmed = raw.trim()
  if (!trimmed) return null

  const compact = stripGroupingSpaces(trimmed)

  // US / d3-style: 52,944,287.72
  if (compact.includes('.') && compact.includes(',')) {
    const parsed = Number(compact.replace(/,/g, ''))
    return Number.isFinite(parsed) ? parsed : null
  }

  // EU-style: 52 944 287,72 or -51,77
  if (compact.includes(',') && !compact.includes('.')) {
    const parsed = Number(compact.replace(',', '.'))
    return Number.isFinite(parsed) ? parsed : null
  }

  const parsed = Number(compact)
  return Number.isFinite(parsed) ? parsed : null
}

export function amountsEqual(a: number, b: number): boolean {
  return Math.abs(a - b) < 0.000001
}
